import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { PlaceholderBar } from '../../../components/loading-skeleton/index';
import { FadeIn } from '../../../components/motion/index';

type EventsHistoryPlaceholderProps = {
  showButton?: boolean;
};

export const EventsHistoryPlaceholder: FC<EventsHistoryPlaceholderProps> = ({
  showButton = true,
}) => (
  <div className="mb-10 flex h-fit w-fit animate-pulse flex-col items-center justify-center text-center">
    <div className="rounded-full p-[12px]">
      <PlaceholderBar className="size-[112px] rounded-full bg-gray-100" />
    </div>
    <div className="flex flex-col items-center gap-2">
      <PlaceholderBar className="h-7 w-48 rounded" />
      <PlaceholderBar className="h-4 w-[230px] rounded" />
    </div>
    {showButton && <PlaceholderBar className="mt-6 h-9 w-32 rounded" />}
  </div>
);

export const EventHistoryRowPlaceholder: FC = () => (
  <div className="flex w-full flex-col gap-3 rounded-md border border-gray-100 bg-white p-4 shadow-sm">
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <PlaceholderBar className="size-10 shrink-0 rounded-full bg-gray-100" />
        <div className="flex min-w-0 flex-col gap-2">
          <PlaceholderBar className="h-5 w-40 max-w-full rounded bg-gray-100" />
          <PlaceholderBar className="h-3 w-28 rounded bg-gray-100" />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <PlaceholderBar className="sm:block hidden h-6 w-20 rounded-full bg-gray-100" />
        <PlaceholderBar className="size-6 rounded bg-gray-100" />
      </div>
    </div>
  </div>
);

type EventsHistorySectionPlaceholderProps = {
  isMobile?: boolean;
  rows?: number;
};

// Rows-only — the real "Event History" header + type tabs stay visible during a reload, so
// the placeholder must not redraw them (that produced a duplicate "two skeletons" header).
export const EventsHistorySectionPlaceholder: FC<EventsHistorySectionPlaceholderProps> = ({
  rows = 4,
}) => (
  <div className="flex w-full animate-pulse flex-col gap-4">
    {Array.from({ length: rows }).map((_, index) => (
      <EventHistoryRowPlaceholder key={index} />
    ))}
  </div>
);

type EventsTransactionViewPlaceholderProps = {
  columns?: number;
  rows?: number;
};

// Transaction-view skeleton — a table that matches the loaded transaction table, so a type
// tab (Fundraising, Grants, ...) shows one continuous table skeleton, not the generic rows.
export const EventsTransactionViewPlaceholder: FC<EventsTransactionViewPlaceholderProps> = ({
  columns = 9,
  rows = 1,
}) => (
  <div className="w-full overflow-x-auto rounded-md border border-gray-100 shadow-sm">
    <table className="w-full animate-pulse">
      <thead className="h-11 border-b border-[#F2F2F2] bg-[#F9FAFB]">
        <tr>
          {Array.from({ length: columns }).map((_, index) => (
            <th className="px-4 text-left" key={index}>
              <PlaceholderBar className="h-4 w-24 rounded bg-gray-100" />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={rowIndex}>
            {Array.from({ length: columns }).map((_cell, cellIndex) => (
              <td className="px-4 py-6" key={cellIndex}>
                <PlaceholderBar className="h-5 w-3/4 rounded bg-gray-100" />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const EventsGraphDeskPlaceholder: FC = () => (
  <div className="flex h-fit w-full animate-pulse flex-col rounded-md p-6 shadow-sm">
    <div className="flex w-full items-center justify-between gap-4">
      <div className="flex flex-col gap-2">
        <PlaceholderBar className="h-4 w-36 rounded bg-gray-100" />
        <PlaceholderBar className="h-3 w-28 rounded bg-gray-100" />
        <div className="mt-2 flex w-full items-center gap-1 border-t border-dashed border-gray-200 pt-2">
          <PlaceholderBar className="h-3 w-48 rounded bg-gray-100" />
          <PlaceholderBar className="size-4 rounded bg-gray-100" />
        </div>
      </div>
      <PlaceholderBar className="h-10 w-[280px] max-w-[40%] rounded-3xl bg-gray-100" />
    </div>
    <PlaceholderBar className="mt-5 h-[370px] w-full rounded-md bg-gray-100" />
  </div>
);

// Full events page skeleton (initial load) — includes the Activity Overview bar and the
// Event History header + type tabs so the whole page reads as a skeleton, like other pages.
export const EventsPagePlaceholder: FC = () => (
  <div className="flex h-fit w-full animate-pulse flex-col gap-4 overflow-hidden p-[1px]">
    <div className="flex w-full items-center justify-between rounded-md p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <PlaceholderBar className="size-6 rounded bg-gray-100" />
        <PlaceholderBar className="h-5 w-36 rounded bg-gray-100" />
      </div>
      <div className="flex gap-3">
        <PlaceholderBar className="h-9 w-24 rounded bg-gray-100" />
        <PlaceholderBar className="h-9 w-28 rounded bg-gray-100" />
      </div>
    </div>

    <EventsGraphDeskPlaceholder />

    <div className="flex w-full flex-col gap-4 rounded-md p-4 shadow-sm">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <PlaceholderBar className="size-5 rounded bg-gray-100" />
          <PlaceholderBar className="h-6 w-32 rounded bg-gray-100" />
        </div>
        <PlaceholderBar className="h-9 w-36 rounded border border-gray-200 bg-gray-100" />
      </div>
      <div className="flex w-full gap-6 overflow-hidden border-b border-gray-200 pb-4">
        {Array.from({ length: 10 }).map((_, index) => (
          <PlaceholderBar className="h-4 w-16 shrink-0 rounded bg-gray-100" key={index} />
        ))}
      </div>
      <EventsHistorySectionPlaceholder rows={4} />
    </div>
  </div>
);

export const EventsMobileSnapshotPlaceholder: FC = () => (
  <div className="flex w-full animate-pulse flex-col gap-2 rounded-lg p-2 shadow-sm">
    <PlaceholderBar className="h-10 w-full rounded-lg" />
    <div className="flex w-full gap-2">
      <PlaceholderBar className="h-16 w-full rounded-lg bg-brand-100" />
      <PlaceholderBar className="h-16 w-full rounded-lg bg-forest-100" />
    </div>
    <div className="flex flex-col gap-2 pt-2">
      <PlaceholderBar className="h-9 w-full rounded-md bg-gray-100" />
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="flex gap-4 px-4 py-3" key={index}>
          <PlaceholderBar className="h-4 w-1/3" />
          <PlaceholderBar className="h-4 w-1/4" />
          <PlaceholderBar className="h-4 w-1/4" />
        </div>
      ))}
    </div>
  </div>
);

export const EventsTransactionDetailPlaceholder: FC = () => (
  <div className="w-full animate-pulse p-[1px]">
    <div className="flex gap-2 py-4">
      <PlaceholderBar className="h-4 w-24" />
      <PlaceholderBar className="h-4 w-32" />
    </div>
    <div className="flex items-center justify-between border-y border-gray-100 py-3">
      <div className="flex items-center gap-2">
        <PlaceholderBar className="size-9 rounded-full" />
        <div className="flex flex-col gap-2">
          <PlaceholderBar className="h-5 w-40" />
          <PlaceholderBar className="h-3 w-24" />
        </div>
      </div>
      <PlaceholderBar className="h-6 w-20 rounded-full" />
    </div>
    <div className="flex flex-col gap-4 pb-1 pt-6">
      {Array.from({ length: 3 }).map((_, index) => (
        <div className="flex flex-col gap-3 rounded-md border border-gray-100 p-4" key={index}>
          <PlaceholderBar className="h-4 w-1/3" />
          <PlaceholderBar className="h-4 w-2/3" />
          <PlaceholderBar className="h-4 w-1/2" />
        </div>
      ))}
    </div>
  </div>
);

const EditEventModalSectionPlaceholder: FC = () => (
  <div className="overflow-hidden rounded-lg bg-white">
    <div className="flex items-center justify-between bg-gray-50 px-4 py-2">
      <PlaceholderBar className="h-4 w-32" />
      <PlaceholderBar className="size-5 rounded" />
    </div>
    <div className="space-y-3 border border-gray-100 px-4 py-3">
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="flex gap-3" key={index}>
          <PlaceholderBar className="h-3 w-24" />
          <PlaceholderBar className="h-3 w-36" />
        </div>
      ))}
    </div>
  </div>
);

export const EditEventModalHeaderPlaceholder: FC = () => (
  <div className="flex min-w-0 flex-col gap-2 pr-2">
    <PlaceholderBar className="h-7 w-36" />
    <PlaceholderBar className="h-4 w-48" />
  </div>
);

export const EditEventModalBodyPlaceholder: FC = () => (
  <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
    <div className="flex w-full shrink-0 animate-pulse flex-col gap-4">
      {Array.from({ length: 3 }).map((_, index) => (
        <EditEventModalSectionPlaceholder key={index} />
      ))}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="flex gap-3 py-2">
          <PlaceholderBar className="h-3 w-24" />
          <PlaceholderBar className="h-3 w-16" />
        </div>
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white px-4 py-3">
        <div className="flex gap-3 py-2">
          <PlaceholderBar className="h-3 w-28" />
          <PlaceholderBar className="h-3 w-8" />
        </div>
      </div>
    </div>
  </div>
);

export const EditEventModalFooterPlaceholder: FC = () => (
  <div className="flex h-9 w-full justify-start gap-3 px-6">
    <PlaceholderBar className="h-9 w-20 rounded" />
  </div>
);

type EventTransactionsTableEmptyStateProps = {
  colSpan: number;
  isFilterResult?: boolean;
};

export const EventTransactionsTableEmptyState: FC<EventTransactionsTableEmptyStateProps> = ({
  colSpan,
  isFilterResult = false,
}) => (
  <tr>
    <td className="p-0" colSpan={colSpan}>
      <FadeIn className="flex w-full flex-col items-center justify-center px-4 py-10 text-center">
        <span className="text-sm font-[550] text-gray-700">
          {isFilterResult ? (
            <AppFormattedMessage id="NO_EVENTS_FOUND" />
          ) : (
            <AppFormattedMessage id="NOTHING_HERE" />
          )}
        </span>
        {isFilterResult && (
          <span className="mt-1 text-xs font-[450] text-gray-500">
            <AppFormattedMessage id="NO_RESULTS_MATCHS" /> Please try again
          </span>
        )}
      </FadeIn>
    </td>
  </tr>
);

type EventTransactionsTableLoadingStateProps = {
  rows?: number;
  visibleColumns: string[];
};

const TRANSACTION_TABLE_CELL_CLASS = 'max-w-[300px] px-4 py-6';
const TRANSACTION_TABLE_ACTION_CELL_CLASS = 'w-12 px-4 py-6';

const renderTransactionLoadingCell = (fieldName: string) => {
  if (fieldName === 'SIGNATURE_STATUS') {
    return (
      <td
        className={twMerge(
          TRANSACTION_TABLE_CELL_CLASS,
          'w-[1%] max-w-[148px] whitespace-nowrap pr-8',
        )}
        key={fieldName}
      >
        <PlaceholderBar className="h-6 w-[88px] rounded-full bg-gray-100" />
      </td>
    );
  }

  if (fieldName === 'FILE') {
    return (
      <td className={TRANSACTION_TABLE_CELL_CLASS} key={fieldName}>
        <PlaceholderBar className="size-5 rounded bg-gray-100" />
      </td>
    );
  }

  if (fieldName === 'EMPTY') {
    return (
      <td className={TRANSACTION_TABLE_ACTION_CELL_CLASS} key={fieldName}>
        <PlaceholderBar className="mx-auto size-6 rounded bg-gray-100" />
      </td>
    );
  }

  return (
    <td className={TRANSACTION_TABLE_CELL_CLASS} key={fieldName}>
      <span className="block truncate text-sm font-[450] text-gray-700">
        <PlaceholderBar className="h-5 max-w-[75%] rounded bg-gray-100" />
      </span>
    </td>
  );
};

export const EventTransactionsTableLoadingState: FC<EventTransactionsTableLoadingStateProps> = ({
  visibleColumns,
  rows = 1,
}) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <tr className="animate-pulse" key={rowIndex}>
        {visibleColumns.map((fieldName) => renderTransactionLoadingCell(fieldName))}
      </tr>
    ))}
  </>
);
